import React, {
    useCallback, useContext, useEffect, useMemo, useState
} from 'react';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { IntlContext, TranslatorContext } from '@jutro/locale';
import { EndorsementService } from 'gw-capability-policychange';
import { ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import _ from 'lodash';
import SgAddon from 'sg-addon';
import {
    SgQuoteContext,
    SgSessionStorageService,
    useSgTrackPageName,
    SgNumberUtil,
    SgActiveTestsContext,
    SgCepChanges
} from 'saga-modules';
import {
    CepConstants
} from 'saga-cep-specific-components';
import { useValidation } from 'gw-portals-validation-react';
import SgCepMotorAddons from 'sg-cep-motor-addons';
import SpmConstants from 'sg-spm-constants';
import SgCepSideMenu from 'sg-cep-side-menu';
import { events } from 'gw-platform-events-js';
import CommonConstants from 'sg-common-constants';
import { Button, Loader } from '@jutro/components';
import { WizardPage } from 'gw-portals-wizard-react';
import metadata from './SgEndorsementCoveragesPage.metadata.json5';
import messages from './SgEndorsementCoveragesPage.messages';

function SgEndorsementCoveragesPage(props) {
    const {
        wizardData: modelVM,
        goNext,
        updateWizardData: updateData,
        sgWizardPropsExt
    } = props;

    const {
        handleCancel,
        handleGoBack
    } = sgWizardPropsExt;

    const {
        isComponentValid,
        onValidate
    } = useValidation('EndorsementCoveragesPage');

    const viewModelService = useContext(ViewModelServiceContext);
    const { activeMvtTests } = useContext(SgActiveTestsContext);
    const intl = useContext(IntlContext);
    const translator = useContext(TranslatorContext);
    const [isLoading, setLoadingState] = useState(false);
    const [quoting, setQuoting] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [isCoverListAvailable, setIsCoverListAvailable] = useState(false);
    const [isCoverListOpened, setIsCoverListOpened] = useState(false);

    const quoteContext = {
        quoting,
        setQuoting
    };

    const getValue = useCallback((value) => {
        const typelistValue = viewModelService.productMetadata.get('pc').types.getTypelist(value.typelist.name).getCode(value.code).name;
        return translator({ id: typelistValue, defaultMessage: typelistValue }).toString();
    }, [translator, viewModelService.productMetadata]);

    const { spmVehicle } = modelVM.lobData.privateMotor.coverables;
    const addOnsCoverages = modelVM.lobData.privateMotor.offerings.children[0].coverages.vehicleCoverages.children[0].coverages.value;
    const changes = SgCepChanges.getMotorChanges(modelVM, getValue);

    useSgTrackPageName(CepConstants.PAGE_NAMES.COVER_OPTIONS);

    function prepareModelForUpdate(pc, initialModel) {
        // extract initial change
        const covs = initialModel.lobData.privateMotor.offerings[0].coverages.vehicleCoverages[0].coverages;
        _.each(covs, (covUpdated) => {
            const termUpdated = covUpdated.terms.find((term) => term.updated);
            if (termUpdated) {
                const term = pc.lobData.privateMotor.offerings[0].coverages.vehicleCoverages[0].coverages
                    .find((cov) => cov.patternCode === covUpdated.patternCode).terms.find((aTerm) => aTerm.patternCode === termUpdated.patternCode);
                term.updated = termUpdated.updated;
                term.chosenTerm = termUpdated.chosenTerm;
                if (term.directValue) {
                    term.directValue = termUpdated.directValue;
                }
            }
            const cov = pc.lobData.privateMotor.offerings[0].coverages.vehicleCoverages[0].coverages
                .find((aCov) => aCov.patternCode === covUpdated.patternCode);
            cov.updated = covUpdated.updated;
            cov.selected = covUpdated.selected;
        });

        return pc.lobData;
    }

    const isBreakdownAddonValid = useCallback(() => {
        const breakdownCoverLevel = addOnsCoverages.find((el) => el.patternCode === SpmConstants.BREAKDOWN_CODE).terms[0].chosenTerm;
        const breakdownAddon = addOnsCoverages.find((el) => el.patternCode === SpmConstants.BREAKDOWN_CODE);
        if (breakdownAddon.selected) {
            return !_.isNil(breakdownCoverLevel);
        }
        return true;
    }, [addOnsCoverages]);

    useEffect(() => {
        const isOnlyNotValidSBAAdded = changes.coverageAdded && changes.coverageAdded.length === 1 && !isBreakdownAddonValid();
        if (Object.keys(changes).length === 0) {
            setIsCoverListOpened(false);
        }
        if (Object.keys(changes).length === 0 || (isOnlyNotValidSBAAdded && !changes.coverageRemoved)) {
            setIsCoverListAvailable(false);
        } else {
            setIsCoverListAvailable(true);
        }
    }, [changes]);

    const onContinue = useCallback(() => {
        if ((!_.isNil(isComponentValid) && !isComponentValid) || !isBreakdownAddonValid()) {
            setShowErrors(true);
            events.emit(CommonConstants.SHOW_VALIDATION_ERROR_EVENT);
            return; // Do not continue
        }
        setLoadingState(true);
        EndorsementService.saveEndorsement(modelVM.value)
            .then((response) => {
                let policyChangeValueCopy = _.cloneDeep(modelVM.value);
                const updatedLobData = prepareModelForUpdate(response, policyChangeValueCopy);
                EndorsementService.updateCoverages(
                    response.jobID,
                    [],
                    { privateMotor: updatedLobData.privateMotor.offerings[0].coverages }
                ).then((result) => {
                    policyChangeValueCopy = _.cloneDeep(modelVM.value);
                    _.merge(policyChangeValueCopy, result);
                    modelVM.value = policyChangeValueCopy;
                    updateData(modelVM);
                    goNext(modelVM);
                }).finally(() => {
                    setLoadingState(false);
                });
            });
    }, [isComponentValid, isBreakdownAddonValid, modelVM, updateData, goNext]);

    const updateCoveragesAndCalculate = useCallback(() => {
        if ((!_.isNil(isComponentValid) && !isComponentValid) || !isBreakdownAddonValid()) {
            setShowErrors(true);
            events.emit(CommonConstants.SHOW_VALIDATION_ERROR_EVENT);
            return; // Do not continue
        }
        setLoadingState(true);
        EndorsementService.saveEndorsement(modelVM.value)
            .then((response) => {
                let policyChangeValueCopy = _.cloneDeep(modelVM.value);
                const updatedLobData = prepareModelForUpdate(response, policyChangeValueCopy);
                EndorsementService.updateCoveragesAndQuote(
                    response.jobID,
                    [],
                    { privateMotor: updatedLobData.privateMotor.offerings[0].coverages }
                ).then((result) => {
                    policyChangeValueCopy = _.cloneDeep(modelVM.value);
                    _.merge(policyChangeValueCopy, result);
                    modelVM.value = policyChangeValueCopy;
                    updateData(modelVM);
                    SgSessionStorageService.setItem(SgSessionStorageService.KEY.MODEL, modelVM.value);
                    setLoadingState(false);
                }).finally(() => {
                    setLoadingState(false);
                });
            });
    }, [isComponentValid, isBreakdownAddonValid, modelVM, updateData]);

    const coverListElements = useCallback((coverages, isAdded) => {
        return coverages.map((coverage) => {
            const currentAmount = addOnsCoverages.find((el) => el.publicID === coverage.publicID).amount;
            return coverage.patternCode === SpmConstants.BREAKDOWN_CODE
                ? (
                    <li key={coverage.publicID}>
                        {coverage.terms.filter((term) => term.patternCode === SpmConstants.BREAKDOWN_LEVEL_CODE)
                            .map((term) => {
                                return (term.chosenTermValue ? (
                                    <div className="sg-sba-cover-list">
                                        <div className="sg-sba-description">
                                            <span>{ coverage.description }</span>
                                            <span className="sg-sba-term">{ term.chosenTermValue }</span>
                                        </div>
                                        <span className="sg-sba-price">
                                            {isAdded ? SgNumberUtil.getCurrencyValue(intl, currentAmount)
                                                : translator(messages.NegativePrice, { price: SgNumberUtil.getCurrencyValue(intl, currentAmount) })}
                                        </span>
                                    </div>
                                ) : <span className="sg-cover-calc-total-due">{ coverage.description }</span>);
                            })}
                    </li>
                ) : (
                    <li key={coverage.publicID}>
                        <span className="sg-cover-calc-total-due">{ coverage.description }</span>
                        <span>
                            {isAdded ? SgNumberUtil.getCurrencyValue(intl, currentAmount)
                                : translator(messages.NegativePrice, { price: SgNumberUtil.getCurrencyValue(intl, currentAmount) })}
                        </span>
                    </li>
                );
        });
    }, [intl, translator, addOnsCoverages]);

    const coverCalculatorContent = useMemo(() => {
        return (
            <div className="sg-cover-calculator-header" id="sgMobileHeader">
                <div className="container">
                    <div className="sg-cover-calc-total">
                        {isCoverListAvailable ? (
                            <Button onClick={() => setIsCoverListOpened(!isCoverListOpened)}>
                                <span className="sg-cover-calc-total-due">
                                    {modelVM.value.transactionCost.amount > 0
                                        ? translator(messages.TotalDueToday) : translator(messages.TotalRefundToday)}
                                </span>
                                {SgNumberUtil.getCurrencyValue(intl, modelVM.value.transactionCost)}
                                {isCoverListOpened ? <i className="gw-icon-caret fa fa-angle-up" />
                                    : <i className="gw-icon-caret fa fa-angle-down" />}
                            </Button>
                        ) : <span className="sg-cover-calc-total-due">{translator(messages.TotalDueZeroToday)}</span>}
                    </div>
                    <ul className={`coverMenuOptions ${isCoverListOpened ? 'visible' : ''}`}>
                        {changes.coverageAdded && coverListElements(changes.coverageAdded, true)}
                        {changes.coverageRemoved && coverListElements(changes.coverageRemoved, false)}
                    </ul>
                </div>
            </div>
        );
    }, [changes.coverageAdded, changes.coverageRemoved, coverListElements, intl,
        isCoverListAvailable, isCoverListOpened, modelVM.value.transactionCost, translator]);

    const overrideProps = {
        vehicleSubHeader: {
            content: `${spmVehicle.value.year} ${spmVehicle.value.make} ${spmVehicle.value.model}`
        },
        cepMotorAddonsComponent: {
            modelVM,
            updateData,
            showErrors,
            onValidate,
            updateCoveragesAndCalculate
        },
        coverCalculator: {
            visible: activeMvtTests.includes(CommonConstants.ACTIVE_MVT_TESTS.WOC_1169),
            content: coverCalculatorContent
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            handleCancel,
            handleGoBack,
            onContinue
        },
        resolveComponentMap: {
            SgAddon,
            SgCepMotorAddons,
            SgCepSideMenu
        }
    };

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }
    return (
        <SgQuoteContext.Provider value={quoteContext}>
            <WizardPage showPrevious={false} showNext={false}>
                {renderContentFromMetadata(metadata.pageContent, overrideProps, resolvers)}
            </WizardPage>
        </SgQuoteContext.Provider>
    );
}

export default SgEndorsementCoveragesPage;
